import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { ApiService, HistoryService } from '@app-core/services';
import { ContractHistoryResponse, ContractType } from '@app-model/api';
import { UserResponse } from '@app-model/login';
import { GetContractHistoryRequest, IUndoDeclineRequest } from '@app-model/request';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import * as HistoryActions from './history.actions';
import * as historyReducer from './history.reducer';
import * as fromRoot from '../reducers';

@Injectable()
export class HistoryEffects {
  private actions$ = inject(Actions);
  private apiService = inject(ApiService);
  private store = inject<Store<fromRoot.State>>(Store);


  getContracts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(HistoryActions.GET_CONTRACTS),
      withLatestFrom(
        this.store.select((state) => state.historyState),
        this.store.select((state) => state.userState.user)
      ),
      mergeMap(([action, , user]: [HistoryActions.GetContractsAction, historyReducer.State, UserResponse]) => {
        const allDealers = JSON.parse(JSON.stringify(user.allDealers));

        if (allDealers && allDealers.length === 1) {
          allDealers[0].cmsDealerNumber = user.cmsDealerNumber;
        }

        const request: GetContractHistoryRequest = {
          allDealers,
          cmsDealerNumber: user.cmsDealerNumber,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          programId: user.programId,
          vin: action.payload.vin,
        };

        return this.apiService.getContractHistory(request).pipe(
          // If successful, dispatch success action with result
          map((response: ContractHistoryResponse) => {
            const filters = HistoryService.fetchHistoryFilters(response.contracts);

            return { type: HistoryActions.GET_CONTRACTS_SUCCESS, payload: { contracts: response, filters } };
          }),
          // If request fails, dispatch failed action
          catchError((payload) => {
            console.error(payload);

            return of({ type: HistoryActions.HISTORY_FAILURE, payload });
          })
        );
      })
    )
  );

  undoDeclineContract$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(HistoryActions.UNDO_DECLINE_CONTRACT),
      withLatestFrom(
        this.store.select((state) => state.historyState),
        this.store.select((state) => state.userState.user)
      ),
      mergeMap(
        ([action, historyState, user]: [
          HistoryActions.UndoDeclineContractAction,
          historyReducer.State,
          UserResponse,
        ]) => {
          const allDealers = JSON.parse(JSON.stringify(user.allDealers));

          if (allDealers && allDealers.length === 1) {
            allDealers[0].cmsDealerNumber = user.cmsDealerNumber;
          }

          const request: IUndoDeclineRequest = {
            allDealers,
            cmsDealerNumber: user.cmsDealerNumber,
            contract: action.payload,
            programId: user.programId,
          };

          return this.apiService.undoDecline(request).pipe(
            // If successful, dispatch success action with result
            map(() => {
              const modifiedContracts = [];

              if (request.contract.status) {
                historyState.contracts.forEach((contract) => {
                  if (contract.econContractNumber === request.contract.econContractNumber) {
                    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

                    const year = new Date().getFullYear();
                    const month = months[new Date().getMonth()];
                    const day = new Date().getDate();

                    modifiedContracts.push({
                      ...contract,
                      modifiedDate: `${month} ${day}, ${year}`,
                      status: 'Pending',
                      statusCode: 'P',
                    });
                  } else {
                    modifiedContracts.push({ ...contract });
                  }
                });
              }

              const contracts: ContractType[] = modifiedContracts.length ? modifiedContracts : historyState.contracts;

              const filters = HistoryService.fetchHistoryFilters(contracts);

              return { type: HistoryActions.UNDO_DECLINE_CONTRACT_SUCCESS, payload: { contracts, filters } };
            }),
            // If request fails, dispatch failed action
            catchError((payload) => {
              console.error(payload);

              return of({ type: HistoryActions.HISTORY_FAILURE, payload });
            })
          );
        }
      )
    )
  );
}
